import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import UpworkLogo from "assets/images/logo-upwork.png";
import FiverrLogo from "assets/images/logo-fiverr.png";
import { fadeUp, staggerContainer } from "components/ui/motion";

const ROTATING = ["Mobile Apps", "Web Platforms", "AI Copilots", "Startup MVPs"];

const TRUST = [
  { value: "50+", label: "Products shipped" },
  { value: "5.0★", label: "Avg. client rating" },
  { value: "7+ yrs", label: "Engineering experience" },
];

const WelcomeBanner = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % ROTATING.length), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden bg-ink-gradient text-white">
      {/* animated glow accents */}
      <motion.div
        className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-500/30 blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-0 right-0 h-96 w-96 rounded-full bg-accent/20 blur-3xl"
        animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      {/* subtle animated grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
        }}
      />

      <motion.div
        className="container-tt relative z-10 flex min-h-[88vh] flex-col justify-center py-24"
        variants={staggerContainer(0.14, 0.1)}
        initial="hidden"
        animate="visible"
      >
        <motion.span className="eyebrow" variants={fadeUp}>
          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
          AI-Enabled Product Studio
        </motion.span>

        <motion.h1
          className="mt-6 max-w-4xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl"
          variants={fadeUp}
        >
          We build intelligent
          <br />
          <span className="relative inline-block min-h-[1.2em]">
            <AnimatePresence mode="wait">
              <motion.span
                key={ROTATING[index]}
                className="inline-block bg-brand-gradient bg-clip-text text-transparent"
                initial={{ opacity: 0, y: 24, rotateX: -40 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -24, rotateX: 40 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {ROTATING[index]}
              </motion.span>
            </AnimatePresence>
          </span>
          <br />
          that think for your users.
        </motion.h1>

        <motion.p className="mt-6 max-w-2xl text-lg text-white/70" variants={fadeUp}>
          Transcend is an AI-first studio crafting mobile &amp; web applications with conversational AI, agents,
          and copilots built in — from validated MVP to production scale.
        </motion.p>

        <motion.div className="mt-9 flex flex-wrap items-center gap-4" variants={fadeUp}>
          <motion.a
            href="#contact"
            className="btn-brand"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Start your project →
          </motion.a>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
            <Link to="/services" className="btn-ghost">
              Explore services
            </Link>
          </motion.div>
        </motion.div>

        {/* trust bar */}
        <motion.div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-6" variants={fadeUp}>
          <div className="flex items-center gap-6">
            {TRUST.map((t) => (
              <div key={t.label}>
                <div className="text-2xl font-semibold text-white">{t.value}</div>
                <div className="text-xs uppercase tracking-wide text-white/50">{t.label}</div>
              </div>
            ))}
          </div>
          <div className="hidden h-10 w-px bg-white/15 sm:block" />
          <div className="flex items-center gap-5 opacity-80">
            <span className="text-xs uppercase tracking-wide text-white/50">Top rated on</span>
            <img src={UpworkLogo} alt="Upwork" className="h-5 w-auto" />
            <img src={FiverrLogo} alt="Fiverr" className="h-5 w-auto" />
          </div>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/40 sm:flex"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default WelcomeBanner;
